/* ==========================================================================
   CUBS — Product page behaviour
   Three small controllers, all safe to run against DOM that Shopify's Section
   Rendering API swaps out from under us (variant changes re-render the block).
   ========================================================================== */
(() => {
  if (window.__cubsPdpInit) return;
  window.__cubsPdpInit = true;

  /* ---------------------------------------------------------------------
     Write helpers

     A MutationObserver re-runs init() whenever the theme swaps DOM in. These
     only touch the DOM when the value actually changes, so init() never
     triggers the observer that scheduled it — without this the page settles
     into a permanent write/observe loop.
     --------------------------------------------------------------------- */

  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  const setData = (element, key, value) => {
    if (element && element.dataset[key] !== value) element.dataset[key] = value;
  };

  /* ---------------------------------------------------------------------
     Horizontal scrollers (product grouping, cart upsells)
     --------------------------------------------------------------------- */

  const syncScroller = (scroller) => {
    const track = scroller.querySelector('[data-cubs-track]');
    const prev = scroller.querySelector('[data-cubs-scroll="prev"]');
    const next = scroller.querySelector('[data-cubs-scroll="next"]');
    if (!track) return;

    // In RTL, scrollLeft runs negative — normalise to a 0..max progress value.
    const max = track.scrollWidth - track.clientWidth;
    const pos = Math.abs(track.scrollLeft);
    const atStart = pos <= 4;
    const atEnd = pos >= max - 4;

    setData(prev, 'disabled', String(atStart));
    setData(next, 'disabled', String(atEnd || max <= 0));
  };

  const stepScroller = (scroller, direction) => {
    const track = scroller.querySelector('[data-cubs-track]');
    if (!track) return;

    const item = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
    const step = item ? item.getBoundingClientRect().width + gap : 160;
    const isRtl = getComputedStyle(track).direction === 'rtl';

    track.scrollBy({ left: direction * step * (isRtl ? -1 : 1), behavior: 'smooth' });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cubs-scroll]');
    if (!button) return;

    const scroller = button.closest('[data-cubs-scroller]');
    if (!scroller) return;

    event.preventDefault();
    stepScroller(scroller, button.dataset.cubsScroll === 'next' ? 1 : -1);
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (!(track instanceof Element) || !track.matches('[data-cubs-track]')) return;

      const scroller = track.closest('[data-cubs-scroller]');
      if (scroller) syncScroller(scroller);
    },
    true
  );

  /* ---------------------------------------------------------------------
     Size guide modal
     --------------------------------------------------------------------- */

  const closeSizeGuides = () => {
    const open = document.querySelectorAll('[data-cubs-sizeguide].is-open');
    if (!open.length) return;

    open.forEach((popup) => popup.classList.remove('is-open'));
    document.body.style.overflow = '';
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cubs-sizeguide-open]');
    if (trigger) {
      const scope = trigger.closest('[data-cubs-tools]');
      const popup = scope && scope.querySelector('[data-cubs-sizeguide]');
      if (popup) {
        popup.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        popup.querySelector('[data-cubs-sizeguide-close]')?.focus();
      }
      return;
    }

    if (event.target.closest('[data-cubs-sizeguide-close]')) closeSizeGuides();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeSizeGuides();
  });

  /* ---------------------------------------------------------------------
     Delivery availability clock
     Which rows exist at all is decided server-side in Liquid from the
     custom.delivery metafield. This only decides whether a rendered row is
     currently "live", and what its status line reads.
     --------------------------------------------------------------------- */

  const zoneParts = (timeZone, date = new Date()) => {
    const parts = {};
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .forEach((part) => {
        if (part.type !== 'literal') parts[part.type] = Number(part.value);
      });
    return parts;
  };

  // Minutes elapsed today, in the shop's timezone.
  const minutesNow = (parts) => parts.hour * 60 + parts.minute;

  const toMinutes = (value) => {
    const [hours, mins] = String(value || '00:00').split(':').map(Number);
    return (hours || 0) * 60 + (mins || 0);
  };

  const weekdayOf = (parts) =>
    new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();

  const shiftDays = (parts, days) => {
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  };

  const isOpenDay = (parts, days, respectFriday, fridayClosed) => {
    const day = weekdayOf(parts);
    if (respectFriday && fridayClosed && day === 5) return false;
    return days.includes(day);
  };

  const nextOpenLabel = (today, days, fridayClosed, locale, timeZone) => {
    for (let offset = 1; offset <= 14; offset += 1) {
      const candidate = shiftDays(today, offset);
      if (!isOpenDay(candidate, days, true, fridayClosed)) continue;
      if (offset === 1) return 'tomorrow';

      const date = new Date(Date.UTC(candidate.year, candidate.month - 1, candidate.day, 12));
      return new Intl.DateTimeFormat(locale, { timeZone, weekday: 'long' }).format(date);
    }
    return '';
  };

  const renderDelivery = (root) => {
    let config;
    try {
      config = JSON.parse(root.querySelector('[data-cubs-delivery-config]').textContent);
    } catch (error) {
      return;
    }

    const { timezone, fridayClosed, text, locale } = config;
    const today = zoneParts(timezone);
    const nowMins = minutesNow(today);

    const express = root.querySelector('[data-cubs-express]');
    if (express) {
      const open =
        isOpenDay(today, config.oneHourDays, false, fridayClosed) &&
        nowMins >= toMinutes(config.oneHourStart) &&
        nowMins < toMinutes(config.oneHourCutoff);

      setText(
        express.querySelector('[data-cubs-status]'),
        open ? text.oneHourAvailable : text.oneHourUnavailable
      );
      express.querySelector('[data-cubs-dot]').classList.toggle('is-live', open);
    }

    const sameDay = root.querySelector('[data-cubs-sameday]');
    if (sameDay) {
      const open =
        isOpenDay(today, config.sameDayDays, true, fridayClosed) &&
        nowMins < toMinutes(config.sameDayCutoff);
      const status = sameDay.querySelector('[data-cubs-status]');
      const dot = sameDay.querySelector('[data-cubs-dot]');

      if (open) {
        setText(status, text.sameDayToday);
        dot.classList.add('is-live');
      } else {
        dot.classList.remove('is-live');
        if (fridayClosed && weekdayOf(today) === 5) {
          setText(status, text.sameDayFriday);
        } else {
          const label = nextOpenLabel(today, config.sameDayDays, fridayClosed, locale, timezone);
          setText(status, (text.sameDayNext || 'Delivered {date}').replace('{date}', label));
        }
      }
    }
  };

  /* ---------------------------------------------------------------------
     Init / re-init
     --------------------------------------------------------------------- */

  const init = () => {
    document.querySelectorAll('[data-cubs-scroller]').forEach(syncScroller);
    document.querySelectorAll('[data-cubs-delivery]').forEach(renderDelivery);
  };

  // Re-run whenever the theme swaps DOM in (variant change, cart update).
  let queued = false;
  const queueInit = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  new MutationObserver(queueInit).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.addEventListener('resize', queueInit);
  // Keep the availability copy honest on long-lived tabs.
  setInterval(init, 60000);
})();
